import PropTypes from 'prop-types'
import React from 'react'
import { injectIntl } from 'react-intl'
import isEmpty from 'lodash/isEmpty'
import findIndex from 'lodash/findIndex'
import matches from 'lodash/matches'
import reject from 'lodash/reject'
import noop from 'lodash/noop'
import classNames from 'classnames'
import { getInstallmentMaxValue } from '../paymentHelper'
import { intlShape } from '@/checkout-instore-legacy/i18n/intlShape'
import PaymentItem from '@/checkout-instore-legacy/components/Commons/PaymentItem'
import { buildChildId } from '@/checkout-instore-legacy/utils/TestUtils'
import { getSellerFriendlyName } from '@/checkout-instore-legacy/seller/utils'
import {
  getNumberOfInstallments,
  getPaymentsPerSeller,
  getPaymentSystemInstallments,
  isExternalPayment,
} from '@/checkout-instore-legacy/utils/PaymentUtils'

import SelectorRadio from './SelectorRadio'
import { isECommercePayment } from '@/checkout-instore-legacy/order/utils'

function PaymentSystemSelector(props) {
  const rejectPaymentSystemOnSelector = (paymentSystem) =>
    paymentSystem.groupName === 'giftCardPaymentGroup' || // remove giftcards on this componente
    (props.filterEcommercePayment &&
      isECommercePayment({ paymentSystem: paymentSystem.stringId })) // remove e-commerce payments when in AddPaymentForm (filterEcommercePayment is passed) since they can only be selected once

  const paymentSystems = reject(
    props.paymentSystems,
    rejectPaymentSystemOnSelector
  )

  const paymentsPerSeller = props.splitPayments
    ? getPaymentsPerSeller(props.paymentsPaid, props.totalsPerSeller)
    : {}

  const selected = (option, index) => {
    switch (typeof props.selected) {
      case 'number':
        return props.selected === index
      case 'function':
        return props.selected(option, index)
      default:
        return props.selected === option
    }
  }

  // This is used to apply props from props.payments to PaymentItem props
  const applyPaymentProps = (paymentSystem) => {
    const paymentIndex = findIndex(
      props.payments,
      matches({
        paymentSystem: String(paymentSystem.id),
      })
    )
    const payment = paymentIndex >= 0 ? props.payments[paymentIndex] : null
    if (!payment) {
      return {
        value: props.value,
        numberOfInstallments: getNumberOfInstallments(
          paymentSystem,
          props.installmentOptions,
          { value: props.value }
        ),
      }
    }
    const installments = getPaymentSystemInstallments(
      paymentSystem,
      props.installmentOptions,
      { value: payment.referenceValue }
    )
    return {
      value: payment.referenceValue,
      showInstallmentPicker: true,
      onSelectInstallment: (installments) =>
        props.onSelectInstallment(payment, paymentIndex, installments),
      selectedInstallment: payment.installments,
      installments: installments,
      numberOfInstallments: installments
        ? getInstallmentMaxValue(installments)
        : null,
    }
  }

  const renderPaymentSystem = (paymentSystem, index) => {
    const isSelected = selected(paymentSystem, index)
    const isExternal = isExternalPayment({
      paymentSystem: paymentSystem.id,
    })
    const isDisabled = isExternal && !isEmpty(props.sellersWithoutPermission)
    const inlineError = isDisabled
      ? props.intl.formatMessage(
          {
            id: 'paymentPermissionError',
          },
          {
            accountName: getSellerFriendlyName(
              props.sellersWithoutPermission[0]
            ),
          }
        )
      : null
    return (
      <div
        key={`option-${index}`}
        className={classNames('bb b--black-10', {
          'hover-bg-light-silver bg-animate pointer': !isDisabled,
        })}
      >
        <PaymentItem
          id={buildChildId(['item', paymentSystem.id], props.id)}
          className={isSelected ? 'bg-light-silver c-action-primary' : ''}
          paymentName={paymentSystem.name}
          paymentSystemId={paymentSystem.id}
          isSelected={isSelected}
          disabled={isDisabled}
          error={inlineError}
          splitPayments={props.splitPayments}
          totalsPerSeller={props.totalsPerSeller}
          paymentsPerSeller={paymentsPerSeller}
          onRefund={props.onRefund}
          groupName={paymentSystem.groupName}
          isAddNewPayment={props.isAddNewPayment}
          onClick={() => props.onSelect(paymentSystem, index, { isSelected })}
          {...applyPaymentProps(paymentSystem)}
        >
          <SelectorRadio
            id={buildChildId(['item-selector', paymentSystem.id], props.id)}
            label=""
            name={props.id}
            value={props.id}
            onChange={noop}
            disabled={isDisabled}
            checked={isSelected}
          />
        </PaymentItem>
      </div>
    )
  }

  if (isEmpty(paymentSystems)) {
    return null
  }

  return (
    <div id={props.id}>
      <section>{paymentSystems.map(renderPaymentSystem)}</section>
    </div>
  )
}

PaymentSystemSelector.defaultProps = {
  filterEcommercePayment: false,
  isAddNewPayment: false,
}

PaymentSystemSelector.propTypes = {
  id: PropTypes.string,
  value: PropTypes.number,
  selected: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.object,
    PropTypes.func,
  ]),
  paymentSystems: PropTypes.array,
  paymentsPaid: PropTypes.array,
  onRefund: PropTypes.func,
  splitPayments: PropTypes.bool.isRequired,
  totalsPerSeller: PropTypes.object.isRequired,
  sellersWithoutPermission: PropTypes.arrayOf(PropTypes.string).isRequired,
  installmentOptions: PropTypes.array,
  onSelect: PropTypes.func.isRequired,

  // (Optional) Use these to display installment picker for selected payments
  filterEcommercePayment: PropTypes.bool,
  isAddNewPayment: PropTypes.bool,
  payments: PropTypes.array,
  onSelectInstallment: PropTypes.func,
  intl: intlShape.isRequired,
}

export default injectIntl(PaymentSystemSelector)
